'use client';
import { BoatBySlugQueryResult } from '@app/sanity.types';
import {
  type StoredBoat,
  useBoatExistsToCompare,
  useComparableBoats,
  useToggleCompare,
} from '@components/CompareBoats/hooks/useCompare';
import { useBoatFavorite } from '@hooks';
import useScrollHandler from '@marine/services/src/ui/useScrollHandler';
import styles from '@styles/SingleBoat/jumbotron.module.scss';
import { getBoatName } from '@utils/getBoatName';
import { Button, Checkbox, Divider } from 'antd';
import cn from 'classnames';
import { AlertTriangle, Eye, FileWarning, Heart } from 'lucide-react';
import Link from 'next/link';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BreadCrumb } from '../BreadCrumb';
import CompareBoats from './Compare';

type Props = {
  setJumbotronHeight: (height: number) => void;
  boat: BoatBySlugQueryResult;
};

/**
 * The public count of how many people favorited this boat.
 *
 * Still not available: it was a field on the Strapi and Lumen boats, and Sanity has no equivalent.
 * A private favorite does not imply a public count, so restoring it needs an aggregate endpoint of
 * its own — see docs/FAVORITES_REST_MIGRATION.md in the backend. Left as a constant so the markup
 * that renders it stays in place for when there is a number to put there.
 */
const favoritesCount = null;

const Jumbotron: React.FC<Props> = ({ setJumbotronHeight, boat }) => {
  const [isHydrates, setIsHydrated] = useState(false);

  const comparableBoats = useComparableBoats();
  const boatExistsToCompare = useBoatExistsToCompare();
  const toggleCompare = useToggleCompare();

  const {
    showToggle: showFavoriteToggle,
    isFavorited,
    isToggling: isTogglingFavorite,
    toggle: toggleFavorite,
  } = useBoatFavorite(boat._id);

  const { viewsCount } = boat;

  const boatTitle = useMemo(() => getBoatName(boat), [boat]);

  // Previously a single hardcoded, unlinked "Boats" crumb, which made every boat page emit the same
  // trail and gave the BreadcrumbList markup nothing page-specific to describe. The last crumb uses
  // the bare model so the maker is not repeated immediately after its own crumb.
  const breadCrumbItems = useMemo(() => {
    const makerName = boat?.manufacturer?.name;
    const makerSlug = boat?.manufacturer?.slug?.current;

    return [
      { title: 'Boats', url: '/boats' },
      ...(makerName && makerSlug ? [{ title: makerName, url: `/maker/${makerSlug}` }] : []),
      { title: boat?.model || boatTitle },
    ];
  }, [boat, boatTitle]);

  const boatIsToCompare = boatExistsToCompare(boat._id) || false;

  /**
   * Only the picker card's worth of this boat. Compare stores an id and re-reads the document from
   * Sanity, so handing it the whole ~90-field boat would just be a copy to throw away.
   */
  const comparableBoat = useMemo<StoredBoat>(
    () => ({
      _id: boat._id,
      attributes: {
        image: boat.image?.asset ? { _type: 'image', asset: boat.image.asset, alt: boat.image.alt } : undefined,
        manufacturer: boat.manufacturer,
        model: boat.model,
        yearFirst: boat.yearFirst,
      },
    }),
    [boat],
  );

  const { stickyHeader } = useScrollHandler();

  const [stickyJumbotron, setStickyJumbotron] = useState<boolean>(false);

  const container = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = (): void => {
      if (!container.current) {
        return;
      }

      setJumbotronHeight(container.current.offsetHeight + 64);

      if (window.pageYOffset > container.current.offsetHeight + 64) {
        setStickyJumbotron(true);
      } else {
        setStickyJumbotron(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setJumbotronHeight]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <section
      className={cn(
        styles.container,
        { [styles.containerSticky]: stickyJumbotron },
        { [styles.containerStickyHeader]: stickyHeader },
      )}
      ref={container}
    >
      <div className={styles.breadcrumbContainer}>
        <BreadCrumb items={breadCrumbItems} />
        {isHydrates && !!comparableBoats?.length && <CompareBoats />}
      </div>

      <div className={styles.containerContent}>
        <h1 className={styles.boatTitle}>{boatTitle}</h1>

        <div className={styles.boatActions}>
          {isHydrates && (
            <Checkbox
              onChange={() => toggleCompare(comparableBoat)}
              className={styles.compareAction}
              checked={boatIsToCompare}
            >
              Add to compare
            </Checkbox>
          )}
          {isHydrates && showFavoriteToggle && (
            <>
              <Divider orientation="vertical" className={styles.divider} />

              <Button
                icon={<Heart size={16} fill={isFavorited ? 'currentColor' : 'none'} />}
                loading={isTogglingFavorite}
                onClick={toggleFavorite}
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                aria-pressed={isFavorited}
                className={cn(styles.saveAction, { [styles.boatFav]: isFavorited })}
              />
            </>
          )}
        </div>

        <div className={styles.boatStatistics}>
          {viewsCount && (
            <p className={styles.boatViews}>
              <Eye size={16} /> {viewsCount}
            </p>
          )}
          {favoritesCount && (
            <p className={styles.boatSaves}>
              <Heart size={16} /> {favoritesCount}
            </p>
          )}
        </div>

        {!stickyJumbotron && (
          <Link
            href="https://support.marinedatacloud.com/portal/en/newticket?departmentId=446184000000145077"
            passHref
            target="_blank"
            rel="noopener noreferrer"
            className={styles.boatReport}
          >
            <AlertTriangle />
            <p>Report error</p>
          </Link>
        )}
      </div>
    </section>
  );
};

export default Jumbotron;
